import React, { useState, useCallback, SyntheticEvent } from 'react';
import { Clickable, Stack, Text, Input, Button, Overlay, ControlLink, RadioButton } from '@moda/om';
import classNames from 'classnames';
import { reportError } from '../../lib/reportError';
import { formatPrice } from '../../lib/formatPrice';
import { useErrorModal } from '../../hooks/useErrorModal';
import { CartPrice } from '../../hooks/useCart';
import { LoadingBalls } from '../LoadingBalls';
import { useGiftCode, getInvalidCodeMessage } from './useGiftCode';

import './GiftCode.scss';

enum Mode {
  Resting,
  Editing,
  Saving
}

interface Props {
  theme?: 'box' | 'simple';
  promotionDiscount: CartPrice;
}

export const GiftCode: React.FC<Props> = ({ theme = 'box', promotionDiscount }) => {
  const [mode, setMode] = useState(Mode.Resting);
  const [inputGiftCode, setInputGiftCode] = useState('');
  const { showErrorModal } = useErrorModal();
  const { giftCode, invalidCodeMessage, applyGiftCode } = useGiftCode();

  const closeEditView = useCallback(() => {
    setMode(Mode.Resting);
    setInputGiftCode('');
  }, [setMode, setInputGiftCode]);

  const submitCode = useCallback(
    async (submittedCode: string | null) => {
      setMode(Mode.Saving);

      try {
        const code = submittedCode?.toUpperCase();
        const result = await applyGiftCode(code || null);
        getInvalidCodeMessage(result.data) ? setMode(Mode.Editing) : closeEditView();
      } catch (error) {
        showErrorModal();
        reportError(error);
      }
    },
    [setMode, applyGiftCode, showErrorModal, closeEditView]
  );

  const onEdit = useCallback(() => setMode(Mode.Editing), [setMode]);

  const onApply = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      inputGiftCode ? submitCode(inputGiftCode) : closeEditView();
    },
    [inputGiftCode, submitCode, closeEditView]
  );

  const onCancel = useCallback(() => (giftCode ? submitCode(null) : closeEditView()), [
    giftCode,
    submitCode,
    closeEditView
  ]);

  return (
    <>
      <Stack
        space={0}
        className={classNames('GiftCode', `GiftCode--${theme}`, { 'GiftCode--empty': !giftCode })}
      >
        <Stack
          space={2}
          className="GiftCode__head"
          direction="horizontal"
          justifyContent="space-between"
        >
          <div className="GiftCode__title">Promo Code {giftCode && `Applied`}</div>
          {!giftCode && mode === Mode.Resting && (
            <ControlLink aria-label="Add Gift Code" onClick={onEdit} data-testid="changeGiftCode">
              + Add Code
            </ControlLink>
          )}
          {promotionDiscount.price > 0 && (
            <div className="GiftCode__content">
              {`-${formatPrice(promotionDiscount.currency, promotionDiscount.price)}`}
            </div>
          )}
        </Stack>
        {giftCode && mode === Mode.Resting && (
          <Stack space={2} direction="horizontal" justifyContent="space-between">
            {mode === Mode.Resting && giftCode && (
              <Text treatment="eyebrow" color="cement">
                {giftCode}
              </Text>
            )}
            <ControlLink aria-label="Add Gift Code" onClick={onEdit} data-testid="changeGiftCode">
              Edit Code
            </ControlLink>
          </Stack>
        )}
        {mode !== Mode.Resting && (
          <form onSubmit={onApply}>
            <Stack space={2} className="GiftCode__apply-section">
              <Text treatment="body2" color="cement">
                Limit one gift code per order
              </Text>
              <Stack space={2}>
                {giftCode && !invalidCodeMessage && (
                  <Stack className="GiftCode__row" direction="horizontal" space={2}>
                    <RadioButton readOnly checked={!inputGiftCode} />
                    <Text treatment="eyebrow" color="cement">
                      {giftCode}
                    </Text>
                  </Stack>
                )}
                <Stack className="GiftCode__row" direction="horizontal" space={2}>
                  {giftCode && <RadioButton readOnly checked={inputGiftCode !== ''} />}
                  <Stack direction="horizontal" space={0}>
                    <Input
                      className="GiftCode__input"
                      error={invalidCodeMessage || false}
                      placeholder="Enter Promo Code"
                      value={inputGiftCode}
                      onChange={event => setInputGiftCode(event.target.value)}
                      data-testid="giftCodeInput"
                    />
                    <Button
                      aria-label="Apply Gift Code"
                      type="submit"
                      data-testid="applyGiftCode"
                      className="GiftCode__apply"
                    >
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
              <Stack space={0} direction="horizontal" justifyContent="space-between">
                {!invalidCodeMessage && (
                  <Clickable
                    className="GiftCode__cancel"
                    onClick={onEdit}
                    data-testid="changeGiftCode"
                  >
                    Cancel
                  </Clickable>
                )}
                {giftCode && (
                  <Clickable
                    type="button"
                    className="GiftCode__cancel"
                    onClick={onCancel}
                    data-testid="cancelGiftCode"
                  >
                    {invalidCodeMessage ? 'Cancel' : 'Remove Code'}
                  </Clickable>
                )}
              </Stack>
            </Stack>
          </form>
        )}
      </Stack>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
